import { useEffect } from 'react';
import { Link } from 'react-router';
import { useScreenStore } from '@/state/screenStore';
import { formatKey } from '@/core/theory/key';
import { useImportStore, type ImportStep, type RemoteStage } from '@/features/upload';
import { formatBytes, formatTime } from '@/lib/format';
import { buttonClass } from '@/ui/button';
import { Cover } from '@/ui/Cover';
import { CheckIcon } from '@/ui/icons';
import styles from './ImportPage.module.css';

const STEPS: { step: ImportStep; label: string; running: string }[] = [
  { step: 'decoding', label: 'Decoding audio', running: 'Reading the file…' },
  {
    step: 'model',
    label: 'Recognizing chords',
    running: 'Listening for chords…',
  },
  { step: 'tuning', label: 'Checking tuning', running: 'Measuring pitch offset…' },
  { step: 'beats', label: 'Finding the beat', running: 'Tracking beats…' },
  { step: 'chords', label: 'Recognizing chords', running: 'Matching chords beat by beat…' },
  { step: 'key', label: 'Detecting key', running: 'Comparing key profiles…' },
];

/** Steps of a song analysed by the server (YouTube links), with a rough share of the wait. */
const REMOTE_STEPS: { step: RemoteStage; label: string; running: string; fraction: number }[] = [
  { step: 'queued', label: 'Waiting for the server', running: 'Starting…', fraction: 0.05 },
  {
    step: 'downloading',
    label: 'Getting the song',
    running: 'The server is fetching the audio…',
    fraction: 0.3,
  },
  {
    step: 'analysing',
    label: 'Recognizing chords',
    running: 'Same engine as for files, on the server…',
    fraction: 0.75,
  },
];

/** The Analyzing screen (design: Analyze.dc.html). */
export function ImportPage() {
  const { status, info, step, fraction, outcome, error, cancel, restart } = useImportStore();
  const { source, remoteStage, queuePosition, usedModel } = useImportStore();
  // Steps in the order they run: the AI model step only when it's used.
  const fileSteps = STEPS.filter((s) => s.step !== 'model' || usedModel).map((s) =>
    usedModel && s.step === 'chords'
      ? { ...s, label: 'Aligning to the beat', running: 'Matching chords to beats…' }
      : s,
  );
  const remote = source !== 'file';
  const { showLibrary, showPlayer } = useScreenStore();
  const songId = status === 'done' ? outcome?.song.id : undefined;
  const nothing = status === 'idle' || !info;

  // Open the player as soon as the chords are ready.
  useEffect(() => {
    if (songId) showPlayer(songId);
    else if (nothing) showLibrary();
  }, [songId, nothing, showPlayer, showLibrary]);

  if (nothing) return null;

  const running = status === 'running';
  const remoteIndex = REMOTE_STEPS.findIndex((s) => s.step === remoteStage);
  const pct = Math.round((remote ? (REMOTE_STEPS[remoteIndex]?.fraction ?? 0) : fraction) * 100);
  const activeIndex = remote ? remoteIndex : fileSteps.findIndex((s) => s.step === step);
  const title = outcome?.song.title ?? info.title ?? info.fileName.replace(/\.[^.]+$/, '');
  const artist = outcome?.song.artist ?? info.artist;
  const cover = outcome?.song.cover ?? info.cover;
  const result = outcome?.result;
  const tuning = result ? Math.round(result.tuningCents) : 0;
  const doneDetail: Record<ImportStep, string> = {
    decoding: 'Decoded locally · mixed to mono',
    model: 'Done',
    tuning:
      tuning === 0
        ? 'In tune with A = 440 Hz'
        : `${tuning > 0 ? '+' : '−'}${Math.abs(tuning)} cents from A = 440 Hz`,
    beats: result
      ? `${Math.round(result.beatGrid.bpm)} BPM · ${result.beatGrid.beatsPerBar}/4`
      : '',
    chords: result ? `${result.segments.filter((s) => s.chord).length} chord changes` : '',
    key: result ? formatKey(result.key) : '',
  };
  const remoteDone = result
    ? `${result.segments.filter((s) => s.chord).length} chord changes · ${formatKey(result.key)}`
    : '';
  const steps = remote
    ? REMOTE_STEPS.map((s, i) => ({
        ...s,
        running:
          s.step === 'queued' && queuePosition > 0
            ? `${queuePosition} ${queuePosition === 1 ? 'song' : 'songs'} ahead…`
            : s.running,
        done: i === REMOTE_STEPS.length - 1 ? remoteDone : 'Done',
      }))
    : fileSteps.map((s) => ({ ...s, done: doneDetail[s.step] }));
  const fileLine = [
    info.fileName,
    info.duration !== undefined ? formatTime(info.duration) : null,
    formatBytes(info.fileSize),
    info.title || info.artist ? "read from the file's tags" : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <main className={styles.page}>
      <Link to="/" className={buttonClass('ghost', styles.back)} onClick={showLibrary}>
        ← Library
      </Link>

      <div className={styles.header}>
        <Cover title={title} src={cover} size={72} tone="success" />
        <div className={styles.headerText}>
          <h1 className={styles.title}>{title}</h1>
          {artist && <span className={styles.artist}>{artist}</span>}
          <span className={styles.fileLine}>{fileLine}</span>
        </div>
      </div>

      <section aria-label="Analysis progress" className={styles.card}>
        {running && (
          <div className={styles.progress}>
            <div className={styles.progressHead}>
              <span className={styles.progressTitle}>
                {remote ? 'Getting chords from the server…' : 'Analyzing on this device…'}
              </span>
              <span className={styles.pct} aria-live="polite">
                {pct}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label="Analysis progress"
              className={styles.bar}
            >
              <div className={styles.barFill} style={{ width: `${pct}%` }} />
            </div>
            <span className={styles.hint}>
              {remote
                ? 'The first time takes up to half a minute; songs the server knows open at once.'
                : 'Runs in the background — you can keep using the app.'}
            </span>
          </div>
        )}

        {status === 'cancelled' && (
          <div className={styles.banner} data-tone="neutral" role="status">
            <span className={styles.bannerStrong}>Analysis cancelled. Nothing was saved.</span>
            <button type="button" className={buttonClass('secondary')} onClick={restart}>
              Start again
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.banner} data-tone="danger" role="alert">
            <span className={styles.bannerStrong}>{error}</span>
            <button type="button" className={buttonClass('secondary')} onClick={restart}>
              Try again
            </button>
            <Link to="/" className={buttonClass('secondary')} onClick={showLibrary}>
              {remote ? 'Back to the library' : 'Choose another file'}
            </Link>
          </div>
        )}

        <ol className={styles.steps}>
          {steps.map((s, i) => {
            const done = status === 'done' || (running && i < activeIndex);
            const active = running && i === activeIndex;
            const detail = done
              ? status === 'done'
                ? s.done
                : 'Done'
              : active
                ? s.running
                : status === 'cancelled' || status === 'error'
                  ? 'Stopped'
                  : 'Waiting';
            return (
              <li
                key={s.step}
                className={styles.step}
                data-state={done ? 'done' : active ? 'active' : 'waiting'}
              >
                <span className={styles.stepIcon}>
                  {done && <CheckIcon size={16} />}
                  {active && <span className={styles.dot} />}
                </span>
                <span className={styles.stepBody}>
                  <span className={styles.stepLabel}>{s.label}</span>
                  <span className={styles.stepDetail}>{detail}</span>
                </span>
              </li>
            );
          })}
        </ol>

        {running && (
          <button
            type="button"
            className={buttonClass('secondary', styles.cancel)}
            onClick={cancel}
          >
            Cancel
          </button>
        )}
      </section>

      <p className={styles.storageNote}>
        {remote
          ? 'The video plays from YouTube; the chords stay in this tab until you close or reload it.'
          : 'Nothing is saved: the song stays in this tab until you close or reload it.'}
      </p>
    </main>
  );
}
